import { useCallback, useId, useState } from 'react'
import { StepProps } from '../../types'
import {
  hasField,
  isFieldRequired,
  isFieldSatisfied,
  getFieldMaxLength,
  getFieldPattern,
} from '../../schema-utils'
import { SchemaBooleanField } from '../../schema-fields'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { Selector } from '@Components/selector/selector'
import { PreviousButton } from '@Components/previous-button/previous-button'

import './claimant-step.scss'

const CLASS_NAME = 'claimant-step'
const SECTION = 'claimant'

export const ClaimantStep = ({
  schema,
  geoData,
  formData,
  onSubmit,
  onBack,
  showBack = true,
}: StepProps) => {
  const existing = (formData.claimant ?? {}) as Record<string, unknown>
  const fieldId = useId()

  const [usCitizen, setUsCitizen] = useState<boolean | undefined>(
    existing.usCitizen as boolean | undefined,
  )
  const [originalClaimant, setOriginalClaimant] = useState<boolean | undefined>(
    existing.originalClaimant as boolean | undefined,
  )
  const [countryOfResidence, setCountryOfResidence] = useState<string>(
    (existing.countryOfResidence as string) ?? '',
  )
  const [firstName, setFirstName] = useState((existing.firstName as string) ?? '')
  const [lastName, setLastName] = useState((existing.lastName as string) ?? '')
  const [middleInitial, setMiddleInitial] = useState((existing.middleInitial as string) ?? '')
  const [suffix, setSuffix] = useState((existing.suffix as string) ?? '')
  const [dateOfBirth, setDateOfBirth] = useState((existing.dateOfBirth as string) ?? '')
  const [explanation, setExplanation] = useState(
    (existing.notOriginalPurchaserExplanation as string) ?? '',
  )

  const countryOptions = geoData.countries.map((c) => ({
    label: c.name,
    value: c.name,
  }))

  const formatSSN = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9)
    const match = digits.match(/^(\d{0,3})(\d{0,2})(\d{0,4})$/)

    if (!match) {
      return ''
    }
    const [, area, group, serial] = match
    if (serial) {
      return `${area}-${group}-${serial}`
    }
    if (group) {
      return `${area}-${group}`
    }
    if (area) {
      return area
    }
    return ''
  }, [])

  const [ssnValue, setSsnValue] = useState(
    existing.socialSecurityNumber ? formatSSN(existing.socialSecurityNumber as string) : '',
  )
  const [revealSsn, setRevealSsn] = useState(false)

  // SSN is required when usCitizen is true. The backend schema marks it optional
  // because non-citizens may omit it, so validate explicitly here against the pattern.
  const ssnPattern = getFieldPattern(schema, SECTION, 'socialSecurityNumber')
  const isSsnSatisfied = (value: string): boolean => {
    if (!value) {
      return false
    }
    if (!ssnPattern) {
      return true
    }
    return new RegExp(ssnPattern).test(value)
  }

  const isValid =
    usCitizen !== undefined &&
    originalClaimant !== undefined &&
    isFieldSatisfied(schema, SECTION, 'firstName', firstName) &&
    isFieldSatisfied(schema, SECTION, 'lastName', lastName) &&
    isFieldSatisfied(schema, SECTION, 'dateOfBirth', dateOfBirth) &&
    (usCitizen === false || isSsnSatisfied(ssnValue)) &&
    (usCitizen === true || countryOfResidence !== '') &&
    (originalClaimant === true ||
      isFieldSatisfied(schema, SECTION, 'notOriginalPurchaserExplanation', explanation))

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return
    }

    const claimant: Record<string, unknown> = {}

    if (hasField(schema, SECTION, 'usCitizen')) {
      claimant.usCitizen = usCitizen
    }
    if (hasField(schema, SECTION, 'originalClaimant')) {
      claimant.originalClaimant = originalClaimant
    }
    if (hasField(schema, SECTION, 'countryOfResidence')) {
      claimant.countryOfResidence = usCitizen === true ? 'United States' : countryOfResidence
    }
    if (hasField(schema, SECTION, 'firstName')) {
      claimant.firstName = firstName
    }
    if (hasField(schema, SECTION, 'lastName')) {
      claimant.lastName = lastName
    }
    if (hasField(schema, SECTION, 'middleInitial') && middleInitial) {
      claimant.middleInitial = middleInitial
    }
    if (hasField(schema, SECTION, 'suffix') && suffix) {
      claimant.suffix = suffix
    }
    if (hasField(schema, SECTION, 'socialSecurityNumber') && usCitizen === true) {
      claimant.socialSecurityNumber = ssnValue
    }
    if (hasField(schema, SECTION, 'dateOfBirth')) {
      claimant.dateOfBirth = dateOfBirth
    }
    if (
      originalClaimant === false &&
      hasField(schema, SECTION, 'notOriginalPurchaserExplanation')
    ) {
      claimant.notOriginalPurchaserExplanation = explanation
    }

    onSubmit({ claimant })
  }, [
    isValid,
    schema,
    usCitizen,
    originalClaimant,
    countryOfResidence,
    firstName,
    lastName,
    middleInitial,
    suffix,
    ssnValue,
    dateOfBirth,
    explanation,
    onSubmit,
  ])

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__section-title`}>Claimant Information</h2>

      <SchemaBooleanField
        schema={schema}
        section={SECTION}
        field="usCitizen"
        value={usCitizen}
        onChange={setUsCitizen}
        className={CLASS_NAME}
      />

      {hasField(schema, SECTION, 'countryOfResidence') && usCitizen === false && (
        <section className={`${CLASS_NAME}__field`}>
          <span id={`${fieldId}-country`} className={`${CLASS_NAME}__label`}>
            Country of Residence *
          </span>
          <Selector<string>
            ariaLabelledBy={`${fieldId}-country`}
            autoComplete="country-name"
            placeHolderText="Select Country..."
            selectables={countryOptions}
            value={countryOfResidence || undefined}
            onValueChange={setCountryOfResidence}
          />
        </section>
      )}

      <SchemaBooleanField
        schema={schema}
        section={SECTION}
        field="originalClaimant"
        value={originalClaimant}
        onChange={setOriginalClaimant}
        className={CLASS_NAME}
      />

      {originalClaimant === false &&
        hasField(schema, SECTION, 'notOriginalPurchaserExplanation') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-explanation`} className={`${CLASS_NAME}__label`}>
              If not, explain{' '}
              {isFieldRequired(schema, SECTION, 'notOriginalPurchaserExplanation') && '*'}
            </label>
            <input
              id={`${fieldId}-explanation`}
              type="text"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'notOriginalPurchaserExplanation')}
              placeholder="Received as gift"
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'firstName') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-first-name`} className={`${CLASS_NAME}__label`}>
              First Name {isFieldRequired(schema, SECTION, 'firstName') && '*'}
            </label>
            <input
              id={`${fieldId}-first-name`}
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'firstName')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
        {hasField(schema, SECTION, 'lastName') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-last-name`} className={`${CLASS_NAME}__label`}>
              Last Name {isFieldRequired(schema, SECTION, 'lastName') && '*'}
            </label>
            <input
              id={`${fieldId}-last-name`}
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'lastName')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'middleInitial') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-middle-initial`} className={`${CLASS_NAME}__label`}>
              Middle Initial
            </label>
            <input
              id={`${fieldId}-middle-initial`}
              type="text"
              autoComplete="additional-name"
              value={middleInitial}
              onChange={(e) => setMiddleInitial(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'middleInitial') ?? 1}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
        {hasField(schema, SECTION, 'suffix') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-suffix`} className={`${CLASS_NAME}__label`}>
              Suffix
            </label>
            <input
              id={`${fieldId}-suffix`}
              type="text"
              autoComplete="honorific-suffix"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'suffix')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      {hasField(schema, SECTION, 'socialSecurityNumber') && usCitizen === true && (
        <section className={`${CLASS_NAME}__field`}>
          <label htmlFor={`${fieldId}-ssn`} className={`${CLASS_NAME}__label`}>
            Social Security Number *
          </label>
          <section className={`${CLASS_NAME}__ssn-input-wrapper`}>
            <input
              id={`${fieldId}-ssn`}
              // Masked by default so the number cannot be read over the claimant's shoulder.
              type={revealSsn ? 'text' : 'password'}
              inputMode="numeric"
              autoComplete="off"
              value={ssnValue}
              onChange={(e) => setSsnValue(formatSSN(e.target.value))}
              placeholder="123-45-6789"
              maxLength={11}
              className={`${CLASS_NAME}__input-control ${CLASS_NAME}__ssn-input`}
            />
            <button
              type="button"
              className={`${CLASS_NAME}__ssn-reveal`}
              aria-label={revealSsn ? 'Hide SSN' : 'Show SSN'}
              onClick={() => setRevealSsn((reveal) => !reveal)}>
              {revealSsn ? 'Hide' : 'Show'}
            </button>
          </section>
          <p className={`${CLASS_NAME}__field-hint`}>
            Protected by Federal Privacy and State Confidentiality Laws.
          </p>
        </section>
      )}

      {hasField(schema, SECTION, 'dateOfBirth') && (
        <section className={`${CLASS_NAME}__field`}>
          <label htmlFor={`${fieldId}-date-of-birth`} className={`${CLASS_NAME}__label`}>
            Date of Birth {isFieldRequired(schema, SECTION, 'dateOfBirth') && '*'}
          </label>
          <input
            id={`${fieldId}-date-of-birth`}
            type="date"
            autoComplete="bday"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className={`${CLASS_NAME}__input-control`}
          />
        </section>
      )}

      <FormFloatingFooter>
        <section className="floating-footer__buttons">
          <PreviousButton show={showBack} onClick={onBack} />
          <Button
            className="floating-footer__next-button"
            disabled={!isValid}
            onClick={handleSubmit}>
            Next
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
